import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext, PROD_TYPES } from '../../context/GlobalContext';
import firestore from '../../firestore';
import firebase from 'firebase/compat/app';
import { Promotion } from '../../hooks/firebase';
import { LEVELS, API_BASE } from '../../utils/constants';
import {
	Tag,
	Info,
	CreditCard,
	Store,
	CheckCircle,
	XCircle,
	DollarSign,
	ChevronDown,
	ChevronUp,
	Plus,
	Pencil,
	X,
	Save,
	Trash2,
	Calendar,
	Clock,
	Filter,
	Search,
	Star,
	Copy,
	Smartphone,
	LayoutGrid,
	Users,
	List,
	ArrowUp,
	ArrowDown,
	ArrowUpDown
} from 'lucide-react';
import { ContainerSpinner } from '../../components/Spinner';
import { Dialog } from '../../components/Dialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import { Button } from '../../shadcn/Button';
import { Checkbox } from '../../shadcn/Checkbox';
import { DateRangePicker } from '../../shadcn/DateRangePicker';
import { DateRange } from 'react-day-picker';
import { validate_cedula, validate_email } from '../../utils/validate';
import { unsafeAPI } from '../../hooks/api';

type BrandModelsData = {
	brand: string;
	models: { id: string; commercial_name: string; model_name: string }[];
}[];

// Format currency
const formatCurrency = (amount: number | undefined, currency_code: string | undefined) => {
	if (amount === undefined || currency_code === undefined) return 'N/A';
	// Assuming currency_code is like 'USD', 'EUR', etc. You might need to adjust this.
	return `${currency_code} ${amount.toFixed(2)}`;
};

const getStartOfDay = (date: Date) => {
	const startOfDay = new Date(date);
	startOfDay.setHours(0, 0, 0, 0);
	return startOfDay;
};

const getEndOfDay = (date: Date) => {
	const endOfDay = new Date(date);
	endOfDay.setHours(23, 59, 59, 999);
	return endOfDay;
};

const normalizePromotionWindow = (promotion: Promotion): Promotion => {
	if (!promotion.promotion_window?.from || !promotion.promotion_window?.to) return promotion;

	return {
		...promotion,
		promotion_window: {
			from: firebase.firestore.Timestamp.fromDate(
				getStartOfDay(new Date(promotion.promotion_window.from.seconds * 1000))
			),
			to: firebase.firestore.Timestamp.fromDate(getEndOfDay(new Date(promotion.promotion_window.to.seconds * 1000)))
		}
	};
};

type PromotionAudience = {
	identification_numbers: string[] | null;
	revision: string;
	copy_from_label?: string;
};

function encode_promotion_value(value: unknown): unknown {
	if (value instanceof firebase.firestore.Timestamp) {
		return { __krece_timestamp: { seconds: value.seconds, nanoseconds: value.nanoseconds } };
	}
	if (typeof value === 'number' && !Number.isFinite(value)) return { __krece_number: String(value) };
	if (Array.isArray(value)) return value.map(encode_promotion_value);
	if (value && typeof value === 'object') {
		return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, encode_promotion_value(item)]));
	}
	return value;
}

// Keep the revision local to this editor; never persist private data in browser storage.
function promotion_revision(promotion: unknown, eligibility: unknown): string {
	const canonical = (value: unknown): unknown => {
		if (Array.isArray(value)) return value.map(canonical);
		if (value && typeof value === 'object') return Object.fromEntries(
			Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, canonical(item)])
		);
		return value;
	};
	return JSON.stringify(canonical(encode_promotion_value([promotion, eligibility])));
}

function promotion_audience_ref(country: string, label: string) {
	// Match Python urllib.parse.quote(..., safe="") in promotion_eligibility.py.
	const code = encodeURIComponent(label.trim()).replace(/[!'()*]/g, (char) =>
		`%${char.charCodeAt(0).toString(16).toUpperCase()}`);
	return firestore.collection('PromotionEligibility').doc(`${country}__${code}`);
}

async function read_promotion_audience(country: string, label: string) {
	return firestore.runTransaction(async (transaction) => {
		const publicSnapshot = await transaction.get(firestore.collection('Promotions').doc(country));
		const privateSnapshot = await transaction.get(promotion_audience_ref(country, label));
		const promotion = publicSnapshot.data()?.[label] as Promotion | undefined;
		if (!promotion || typeof promotion.label !== 'string') throw new Error('La promoción ya no existe. Recargue la lista.');
		const eligibility = privateSnapshot.exists ? privateSnapshot.data() : null;
		if (privateSnapshot.exists && (!eligibility || eligibility.country !== country ||
			eligibility.promotion_code !== label.trim() || !Array.isArray(eligibility.identification_numbers) ||
			!eligibility.identification_numbers.every((number: unknown) => typeof number === 'string'))) {
			throw new Error('La lista privada es inválida. No se puede editar esta promoción.');
		}
		return { promotion, identification_numbers: eligibility?.identification_numbers as string[] | undefined,
			restricted: privateSnapshot.exists, revision: promotion_revision(promotion, eligibility) };
	});
}

async function write_promotion_audience(
	country: string, label: string, original: string, audience: PromotionAudience, promotion: Promotion | null
) {
	const source = original || audience.copy_from_label || '';
	if (!label.trim() || label !== label.trim() || label.length > 120) throw new Error('Nombre de promoción inválido.');
	if (!source && audience.revision !== 'new') throw new Error('Revisión inválida. Vuelva a abrir el editor.');
	const publicRef = firestore.collection('Promotions').doc(country);
	const targetRef = promotion_audience_ref(country, label);
	const sourceRef = source ? promotion_audience_ref(country, source) : targetRef;
	await firestore.runTransaction(async (transaction) => {
		const publicSnapshot = await transaction.get(publicRef);
		const targetSnapshot = await transaction.get(targetRef);
		const sourceSnapshot = sourceRef.id === targetRef.id ? targetSnapshot : await transaction.get(sourceRef);
		if (!publicSnapshot.exists) throw new Error('No existe la configuración de promociones del país.');
		const promotions = publicSnapshot.data() || {};
		if (source && (!promotions[source] || promotion_revision(promotions[source], sourceSnapshot.exists ? sourceSnapshot.data() : null) !== audience.revision)) {
			throw new Error('La promoción cambió. Cierre el editor y vuelva a abrirla.');
		}
		const target = targetSnapshot.data();
		const retired = target?.retired === true && target.country === country && target.promotion_code === label &&
			Array.isArray(target.identification_numbers) && target.identification_numbers.length === 0;
		if (promotion && label !== original && (Object.prototype.hasOwnProperty.call(promotions, label) ||
			(targetSnapshot.exists && !retired && (!source || sourceRef.id !== targetRef.id)))) {
			throw new Error('Ya existe una promoción o lista privada con ese nombre. Use otro nombre.');
		}
		transaction.update(publicRef, new firebase.firestore.FieldPath(label), promotion || firebase.firestore.FieldValue.delete());
		const retire = (code: string) => ({ country, promotion_code: code.trim(), identification_numbers: [], retired: true });
		if (!promotion) transaction.set(targetRef, retire(label));
		else if (audience.identification_numbers === null) transaction.delete(targetRef);
		else transaction.set(targetRef, { country, promotion_code: label, identification_numbers: audience.identification_numbers });
		if (original && original !== label) {
			transaction.update(publicRef, new firebase.firestore.FieldPath(original), firebase.firestore.FieldValue.delete());
			transaction.set(sourceRef, retire(original));
		}
	});
}

function add_promotion_identifications(input: string, existing: string[], country: string, prefix: string): string[] {
	const canonical = (value: string) => {
		const raw = value.trim().toUpperCase();
		return prefix && !raw.startsWith(prefix) ? prefix + raw : raw;
	};
	const additions = input.split(/[\n,;]+/).map((value) => value.trim().toUpperCase()).filter(Boolean);
	if (additions.some((value) => !validate_cedula(prefix && value.startsWith(prefix) ? value.slice(prefix.length) : value, country))) {
		throw new Error('Hay cédulas con formato inválido para el país seleccionado. Revise la lista.');
	}
	const result = Array.from(new Set([...existing.map(canonical), ...additions.map(canonical)]));
	if (result.length > 5000) throw new Error('Puede agregar como máximo 5000 cédulas por promoción.');
	return result;
}

// Edit Dialog Component
function EditPromotionDialog({
	promotion,
	sourceLabel,
	onClose,
	onSave,
	onDelete,
	currency_code,
	saving,
	deleting,
	isNew
}: {
	promotion: Promotion;
	sourceLabel: string | null;
	onClose: () => void;
	onSave: (updatedPromotion: Promotion, originalLabel: string, audience: PromotionAudience) => void;
	onDelete?: (promotionLabel: string, revision: string) => void;
	currency_code: string | undefined;
	saving: boolean;
	deleting?: boolean;
	isNew?: boolean;
}) {
	const { userData, internalCountry, CountrySettings } = useContext(GlobalContext);
	const [editedPromotion, setEditedPromotion] = useState<Promotion>({
		...promotion,
		levels: !promotion.levels || promotion.levels.length === 0 ? [...LEVELS] : promotion.levels
	});
	const originalLabel = isNew ? '' : sourceLabel || promotion.label;
	const [newStore, setNewStore] = useState('');
	const [newExcludedStore, setNewExcludedStore] = useState('');
	const [newImeiTac, setNewImeiTac] = useState('');
	const [newPaymentOption, setNewPaymentOption] = useState({ installments: '', factor: 0 });
	const [newPaymentOptionFactor, setNewPaymentOptionFactor] = useState('');
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
	const country = internalCountry?.get() || 'venezuela';
	const audienceScope = JSON.stringify([country, sourceLabel, userData?.email]);
	const [audienceLoadedFor, setAudienceLoadedFor] = useState<string | null>(null);
	const [audienceError, setAudienceError] = useState<string | null>(null);
	const [audienceRetry, setAudienceRetry] = useState(0);
	const [audienceRevision, setAudienceRevision] = useState('new');
	const [restricted, setRestricted] = useState(false);
	const [identificationNumbers, setIdentificationNumbers] = useState<string[]>([]);
	const [newIdentifications, setNewIdentifications] = useState('');
	const [showPublicConfirm, setShowPublicConfirm] = useState(false);
	const audienceReady = audienceLoadedFor === audienceScope;

	useEffect(() => {
		let cancelled = false;
		setAudienceLoadedFor(null);
		setAudienceError(null);
		setNewIdentifications('');
		(async () => {
			try {
				if (!userData) throw new Error('No hay una sesión válida para cargar las cédulas.');
				if (!sourceLabel) {
					setRestricted(false);
					setIdentificationNumbers([]);
					setAudienceRevision('new');
				} else {
					const data = await read_promotion_audience(country, sourceLabel);
					if (cancelled) return;
					const fresh = data.promotion;
					setEditedPromotion({
						...fresh,
						levels: fresh.levels?.length ? fresh.levels : [...LEVELS],
						...(isNew ? { label: promotion.label, is_active: false } : {})
					});
					setRestricted(data.restricted);
					setIdentificationNumbers(Array.from(new Set(data.identification_numbers || [])));
					setAudienceRevision(data.revision);
				}
				if (!cancelled) setAudienceLoadedFor(audienceScope);
			} catch (error) {
				if (!cancelled) setAudienceError(error instanceof Error ? error.message : 'No se pudieron cargar las cédulas.');
			}
		})();
		return () => { cancelled = true; };
		// Secondary store snapshots must not replace a draft the admin is editing.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [audienceScope, audienceRetry]);

	const addIdentifications = (input = newIdentifications) => {
		try {
			const values = add_promotion_identifications(
				input, identificationNumbers, country, CountrySettings?.uid_prefix?.toUpperCase() || ''
			);
			setIdentificationNumbers(values);
			setNewIdentifications('');
			return values;
		} catch (error) {
			setAlertDialog({ title: 'Revise las cédulas', message: error instanceof Error ? error.message : 'Lista inválida.' });
			return null;
		}
	};



	// Brands/Models state
	const [brandsModelsData, setBrandsModelsData] = useState<BrandModelsData>([]);
	const [brandsModelsLoading, setBrandsModelsLoading] = useState(false);
	const [brandsModelsSearch, setBrandsModelsSearch] = useState('');
	const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());

	useEffect(() => {
		(async () => {
			setBrandsModelsLoading(true);
			const res = await unsafeAPI('GET', `${API_BASE}/device_brands_models/`, null, userData, internalCountry?.get());
			if (res?.success && res.data) {
				setBrandsModelsData(res.data);
				// Auto-expand brands that have selected models
				const selectedModels = new Set(editedPromotion.models || []);
				const toExpand = new Set<string>();
				for (const brand of res.data) {
					if (brand.models.some((m: { id: string }) => selectedModels.has(m.id))) {
						toExpand.add(brand.brand);
					}
				}
				if (toExpand.size > 0) setExpandedBrands(toExpand);
			}
			setBrandsModelsLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filteredBrandsModels = useMemo(() => {
		if (!brandsModelsSearch.trim()) return brandsModelsData;
		const q = brandsModelsSearch.toLowerCase();
		return brandsModelsData
			.map((brand) => {
				const brandMatch = brand.brand.toLowerCase().includes(q);
				const matchingModels = brand.models.filter(
					(m) => m.commercial_name.toLowerCase().includes(q) || m.model_name.toLowerCase().includes(q)
				);
				if (brandMatch) return brand;
				if (matchingModels.length > 0) return { ...brand, models: matchingModels };
				return null;
			})
			.filter(Boolean) as BrandModelsData;
	}, [brandsModelsData, brandsModelsSearch]);

	const isBrandSelected = (brandName: string) => editedPromotion.brands?.includes(brandName) || false;

	const toggleBrand = (brandName: string) => {
		setEditedPromotion((prev) => {
			const currentBrands = prev.brands || [];
			if (currentBrands.includes(brandName)) {
				// Deselect brand - also remove its models from individual selections
				const brandModels = brandsModelsData.find((b) => b.brand === brandName);
				const brandModelIds = new Set(brandModels?.models.map((m) => m.id) || []);
				return {
					...prev,
					brands: currentBrands.filter((b) => b !== brandName),
					models: (prev.models || []).filter((m) => !brandModelIds.has(m))
				};
			} else {
				// Select brand - remove individual model selections for this brand (brand covers all)
				const brandModels = brandsModelsData.find((b) => b.brand === brandName);
				const brandModelIds = new Set(brandModels?.models.map((m) => m.id) || []);
				return {
					...prev,
					brands: [...currentBrands, brandName],
					models: (prev.models || []).filter((m) => !brandModelIds.has(m))
				};
			}
		});
	};

	const isModelSelected = (modelId: string, brandName: string) => {
		if (editedPromotion.brands?.includes(brandName)) return true;
		return editedPromotion.models?.includes(modelId) || false;
	};

	const toggleModel = (modelId: string, brandName: string) => {
		// If brand is selected, don't allow toggling individual models
		if (editedPromotion.brands?.includes(brandName)) return;
		setEditedPromotion((prev) => {
			const currentModels = prev.models || [];
			if (currentModels.includes(modelId)) {
				return { ...prev, models: currentModels.filter((m) => m !== modelId) };
			} else {
				return { ...prev, models: [...currentModels, modelId] };
			}
		});
	};

	const toggleExpandBrand = (brandName: string) => {
		setExpandedBrands((prev) => {
			const next = new Set(prev);
			if (next.has(brandName)) next.delete(brandName);
			else next.add(brandName);
			return next;
		});
	};

	const totalBrandsModelsSelected =
		(editedPromotion.brands?.length || 0) + (editedPromotion.models?.length || 0);

	const clearBrandsModels = () => {
		setEditedPromotion((prev) => ({ ...prev, brands: [], models: [] }));
	};

	const handleSave = () => {
		if (!audienceReady || saving || deleting) return;
		// Validate required fields
		if (!editedPromotion.label?.trim()) {
			setAlertDialog({ title: 'Error', message: 'El nombre de la promoción es requerido' });
			return;
		}
		if (!editedPromotion.description?.trim()) {
			setAlertDialog({ title: 'Error', message: 'La descripción de la promoción es requerida' });
			return;
		}
		if (!editedPromotion.levels || editedPromotion.levels.length === 0) {
			setAlertDialog({ title: 'Error', message: 'Debe seleccionar al menos un nivel' });
			return;
		}
		const numbers = restricted && newIdentifications.trim() ? addIdentifications() : identificationNumbers;
		if (restricted && numbers === null) return;
		const promotionToSave = normalizePromotionWindow(editedPromotion);
		onSave(promotionToSave, originalLabel, {
			identification_numbers: restricted ? numbers : null,
			revision: audienceRevision,
			...(isNew && sourceLabel ? { copy_from_label: sourceLabel } : {})
		});
	};

	const handleDelete = () => {
		setShowDeleteConfirm(true);
	};

	const handleConfirmDelete = async (): Promise<true | string> => {
		if (onDelete) {
			onDelete(originalLabel, audienceRevision);
			return true;
		}
		return true;
	};

	const addStore = () => {
		if (newStore.trim()) {
			// Split by newlines, commas, or semicolons and filter out empty strings
			const storeList = Array.from(
				new Set(
					newStore
						.split(/[\n,;]+/)
						.map((store) => store.trim())
						.filter((store) => store.length > 0)
				)
			);

			// Validate all emails
			const invalidEmails = storeList.filter(store => !validate_email(store));
			if (invalidEmails.length > 0) {
				setAlertDialog({ 
					title: 'Emails inválidos', 
					message: `Los siguientes emails no son válidos: ${invalidEmails.join(', ')}` 
				});
				return;
			}

			if (storeList.length > 0) {
				setEditedPromotion((prev) => ({
					...prev,
					stores: Array.from(new Set([...(prev.stores || []), ...storeList]))
				}));
				setNewStore('');
			}
		}
	};

	const removeStore = (index: number) => {
		setEditedPromotion((prev) => ({
			...prev,
			stores: prev.stores?.filter((_, i) => i !== index) || []
		}));
	};

	const addExcludedStore = () => {
		if (newExcludedStore.trim()) {
			// Split by newlines, commas, or semicolons and filter out empty strings
			const storeList = Array.from(
				new Set(
					newExcludedStore
						.split(/[\n,;]+/)
						.map((store) => store.trim())
						.filter((store) => store.length > 0)
				)
			);

			// Validate all emails
			const invalidEmails = storeList.filter(store => !validate_email(store));
			if (invalidEmails.length > 0) {
				setAlertDialog({ 
					title: 'Emails inválidos', 
					message: `Los siguientes emails no son válidos: ${invalidEmails.join(', ')}` 
				});
				return;
			}

			if (storeList.length > 0) {
				setEditedPromotion((prev) => ({
					...prev,
					excluded_stores: Array.from(new Set([...(prev.excluded_stores || []), ...storeList]))
				}));
				setNewExcludedStore('');
			}
		}
	};

	const removeExcludedStore = (index: number) => {
		setEditedPromotion((prev) => ({
			...prev,
			excluded_stores: prev.excluded_stores?.filter((_, i) => i !== index) || []
		}));
	};

	const addImeiTac = () => {
		if (newImeiTac.trim()) {
			// Split by newlines, commas, or semicolons and filter out empty strings
			const imeiList = Array.from(
				new Set(
					newImeiTac
						.split(/[\n,;]+/)
						.map((imei) => imei.trim())
						.filter((imei) => imei.length > 0)
				)
			);

			if (imeiList.length > 0) {
				setEditedPromotion((prev) => ({
					...prev,
					imei_tacs: Array.from(new Set([...(prev.imei_tacs || []), ...imeiList]))
				}));
				setNewImeiTac('');
			}
		}
	};

	const removeImeiTac = (index: number) => {
		setEditedPromotion((prev) => ({
			...prev,
			imei_tacs: prev.imei_tacs?.filter((_, i) => i !== index) || []
		}));
	};

	const addPaymentOption = () => {
		const installments = parseInt(newPaymentOption.installments);
		const factor = parseFloat(newPaymentOption.factor.toString());

		if (!isNaN(installments) && !isNaN(factor) && installments > 0 && factor > 0) {
			setEditedPromotion((prev) => ({
				...prev,
				payment_options: [...(prev.payment_options || []), { installments, factor }]
			}));
			setNewPaymentOption({ installments: '', factor: 0 });
		}
	};

	const removePaymentOption = (index: number) => {
		setEditedPromotion((prev) => ({
			...prev,
			payment_options: prev.payment_options?.filter((_, i) => i !== index) || []
		}));
	};

	const toggleProductType = (productType: (typeof PROD_TYPES)[number]) => {
		setEditedPromotion((prev) => {
			const currentTypes = prev.product_types || [];
			if (currentTypes.includes(productType)) {
				return {
					...prev,
					product_types: currentTypes.filter((t) => t !== productType)
				};
			} else {
				return {
					...prev,
					product_types: [...currentTypes, productType]
				};
			}
		});
	};

	const toggleLevel = (level: (typeof LEVELS)[number]) => {
		setEditedPromotion((prev) => {
			const currentLevels = prev.levels || [];
			if (currentLevels.includes(level)) {
				return {
					...prev,
					levels: currentLevels.filter((l) => l !== level)
				};
			} else {
				return {
					...prev,
					levels: [...currentLevels, level]
				};
			}
		});
	};

	const levelLabels: Record<(typeof LEVELS)[number], string> = {
		level_1: 'Krece Azul',
		level_2: 'Krece Plata',
		level_3: 'Krece Oro',
		level_4: 'Krece Platino'
	};

	if (!audienceReady) {
		return <Dialog title="Cédulas de la promoción" onClose={onClose} closable={true}>
			{audienceError ? <div className="space-y-4">
				<p className="text-sm text-neutral-200">{audienceError}</p>
				<Button variant="secondary" onClick={() => setAudienceRetry((value) => value + 1)}>Volver a intentar</Button>
			</div> : <><p className="text-sm text-neutral-300">Cargando configuración privada...</p><ContainerSpinner size={2} /></>}
		</Dialog>;
	}

	return (
		<>
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			{showPublicConfirm && (
				<ConfirmDialog
					title="Quitar restricción por cédula"
					message="La promoción dejará de estar limitada a esta lista cuando guarde. Se mantendrán sus demás condiciones. ¿Desea continuar?"
					onConfirm={async () => { setRestricted(false); return true; }}
					onClose={() => setShowPublicConfirm(false)}
				/>
			)}
			{showDeleteConfirm && (
				<ConfirmDialog
					title="Eliminar Promoción"
					message="¿Estás seguro de que deseas eliminar esta promoción? Esta acción no se puede deshacer."
					onConfirm={handleConfirmDelete}
					onClose={() => setShowDeleteConfirm(false)}
				/>
			)}
			<Dialog
				title=""
				onClose={onClose}
				closable={!saving && !deleting}
				cerrarButton={false}
				className="!w-[min(700px,95%)] !p-2 md:!p-6"
			>
				<div className="space-y-8 p-1 pb-20 md:pb-16">
					{/* Basic Information */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-primary-500/10 p-2">
								<Tag className="h-5 w-5 text-primary-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Información General</h3>
						</div>

						<div className="grid gap-4">
							<Input
								floatingLabel="Nombre de la promoción"
								placeholder="Ingrese el nombre"
								value={editedPromotion.label}
								onChange={(value) => setEditedPromotion((prev) => ({ ...prev, label: value }))}
							/>

							<Input
								floatingLabel="Descripción"
								placeholder="Ingrese la descripción"
								value={editedPromotion.description || ''}
								onChange={(value) => setEditedPromotion((prev) => ({ ...prev, description: value }))}
							/>

							<Select
								floatingLabel="Estado"
								options={[
									{ label: 'Activa', value: true },
									{ label: 'Inactiva', value: false }
								]}
								defaultValue={editedPromotion.is_active !== false}
								onSelectedChange={(value) => setEditedPromotion((prev) => ({ ...prev, is_active: value as boolean }))}
							/>

							<DateRangePicker
								defaultValue={
									editedPromotion.promotion_window?.from && editedPromotion.promotion_window?.to
										? {
												from: new Date(editedPromotion.promotion_window.from.seconds * 1000),
												to: new Date(editedPromotion.promotion_window.to.seconds * 1000)
											}
										: undefined
								}
								floatingLabel="Período de vigencia"
								className="w-full"
								popoverContentClassName="pointer-events-auto"
								onRangeChange={(range: DateRange | undefined) => {
									if (range?.from && range?.to) {
										setEditedPromotion((prev) => ({
											...prev,
											promotion_window: {
												from: firebase.firestore.Timestamp.fromDate(getStartOfDay(range.from!)),
												to: firebase.firestore.Timestamp.fromDate(getEndOfDay(range.to!))
											}
										}));
									} else {
										setEditedPromotion((prev) => ({
											...prev,
											promotion_window: undefined
										}));
									}
								}}
							/>
						</div>
					</div>

					{/* Financial Information */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-green-500/10 p-2">
								<DollarSign className="h-5 w-5 text-green-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Información Financiera</h3>
						</div>

						<div className="grid gap-4 md:grid-cols-2">
							<Input
								floatingLabel="Porcentaje inicial (%)"
								placeholder="Ej: 30"
								value={
									editedPromotion.initial_percentage !== undefined
										? (editedPromotion.initial_percentage * 100).toString()
										: ''
								}
								onChange={(value) => {
									const percentage = parseFloat(value);
									if (!isNaN(percentage)) {
										setEditedPromotion((prev) => ({ ...prev, initial_percentage: percentage / 100 }));
									} else if (value === '') {
										setEditedPromotion((prev) => ({ ...prev, initial_percentage: undefined }));
									}
								}}
								validation={(value) => {
									const num = parseFloat(value);
									return value === '' || (!isNaN(num) && num >= 0 && num <= 100);
								}}
								allowedChars="numeric"
							/>

							<Input
								floatingLabel={`Precio mínimo (${currency_code || '$'})`}
								placeholder="Ej: 100"
								value={editedPromotion.min_price?.toString() || ''}
								onChange={(value) => {
									const price = parseFloat(value);
									if (!isNaN(price)) {
										setEditedPromotion((prev) => ({ ...prev, min_price: price }));
									} else if (value === '') {
										setEditedPromotion((prev) => ({ ...prev, min_price: undefined }));
									}
								}}
								allowedChars="numeric"
							/>
						</div>

						<Input
							floatingLabel={`Crédito aprobado (${currency_code || '$'})`}
							placeholder="Ej: Infinity"
							value={editedPromotion.approved_credit?.toString() || ''}
							onChange={(value) => {
								if (value.toLowerCase() === 'infinity') {
									setEditedPromotion((prev) => ({ ...prev, approved_credit: Infinity }));
								} else {
									const credit = parseFloat(value);
									if (!isNaN(credit)) {
										setEditedPromotion((prev) => ({ ...prev, approved_credit: credit }));
									} else if (value === '') {
										setEditedPromotion((prev) => ({ ...prev, approved_credit: undefined }));
									}
								}
							}}
							allowedChars="numbers_letters_dot_comma"
						/>
					</div>

					{/* Payment Options */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-blue-500/10 p-2">
								<CreditCard className="h-5 w-5 text-blue-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Planes de Pago</h3>
						</div>

						{editedPromotion.payment_options && editedPromotion.payment_options.length > 0 && (
							<div className="space-y-3">
								{editedPromotion.payment_options.map((option, idx) => (
									<div
										key={idx}
										className="group flex items-center justify-between rounded-xl border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-4 transition-all hover:border-neutral-700"
									>
										<div className="flex items-center gap-3">
											<div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/20 text-xs font-semibold text-blue-400">
												{option.installments}
											</div>
											<div>
												<span className="text-sm font-medium text-neutral-200">{option.installments} cuotas</span>
												<div className="text-xs text-neutral-400">
													{(option.factor * 100 - 100) % 1 === 0
														? (option.factor * 100 - 100).toFixed(0)
														: (option.factor * 100 - 100).toFixed(1)}
													% interés
												</div>
											</div>
										</div>
										<button
											onClick={() => removePaymentOption(idx)}
											className="rounded-full p-2 text-neutral-500 opacity-0 transition-all hover:bg-red-500/10 hover:text-red-400 group-hover:opacity-100"
										>
											<X className="h-4 w-4" />
										</button>
									</div>
								))}
							</div>
						)}

						<div className="rounded-xl border border-dashed border-neutral-700 bg-neutral-900/30 p-4">
							<div className="space-y-3">
								<div className="grid grid-cols-2 gap-3">
									<Input
										placeholder="Cuotas"
										value={newPaymentOption.installments}
										onChange={(value) => setNewPaymentOption((prev) => ({ ...prev, installments: value }))}
										allowedChars="numbers_only"
										containerClassName="w-full"
									/>
									<Input
										placeholder="Interés (%)"
										value={newPaymentOptionFactor}
										onChange={(value) => {
											setNewPaymentOptionFactor(!value || parseFloat(value) > 100 || parseFloat(value) < 0 ? '' : value);
											// Convert percentage (0-100) to factor (1.00-2.00)
											const percentage = parseFloat(value);
											if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
												const factor = (1 + percentage / 100).toFixed(2);
												setNewPaymentOption((prev) => ({ ...prev, factor: parseFloat(factor) }));
											} else if (value === '') {
												setNewPaymentOption((prev) => ({ ...prev, factor: 0 }));
											}
										}}
										allowedChars="numbers_only"
										containerClassName="w-full"
									/>
								</div>
								<Button variant="secondary" onClick={addPaymentOption} className="w-full">
									<Plus className="h-4 w-4" />
									Agregar Plan
								</Button>
							</div>
						</div>
					</div>

					{/* Product Types */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-purple-500/10 p-2">
								<Info className="h-5 w-5 text-purple-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Tipos de Producto</h3>
						</div>

						<div className="grid grid-cols-2 gap-3">
							{PROD_TYPES.map((productType) => (
								<label
									key={productType}
									className="group flex cursor-pointer items-center gap-3 rounded-xl border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-4 transition-all hover:border-neutral-700"
								>
									<Checkbox
										checked={editedPromotion.product_types?.includes(productType) || false}
										onCheckedChange={() => toggleProductType(productType)}
									/>
									<span className="text-sm font-medium capitalize text-neutral-200 group-hover:text-neutral-100">
										{productType === 'iphone' ? 'iPhone' : productType}
									</span>
								</label>
							))}
						</div>
					</div>

					{/* Levels */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-amber-500/10 p-2">
								<Star className="h-5 w-5 text-amber-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Niveles Aplicables</h3>
						</div>

						<div className="grid grid-cols-2 gap-3">
							{LEVELS.map((level) => (
								<label
									key={level}
									className="group flex cursor-pointer items-center gap-3 rounded-xl border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-4 transition-all hover:border-neutral-700"
								>
									<Checkbox
										checked={editedPromotion.levels?.includes(level) || false}
										onCheckedChange={() => toggleLevel(level)}
									/>
									<span className="text-sm font-medium text-neutral-200 group-hover:text-neutral-100">
										{levelLabels[level]}
									</span>
								</label>
							))}
						</div>
					</div>

					{/* Stores */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-primary-500/10 p-2">
								<Store className="h-5 w-5 text-primary-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Tiendas Aplicables</h3>
						</div>

						{/* All stores checkbox */}
						<label className="group flex cursor-pointer items-center gap-3 rounded-xl border border-neutral-800 bg-gradient-to-r from-orange-900/20 to-orange-800/10 p-4 transition-all hover:border-orange-700/50 light:from-orange-700/10 light:to-orange-700/5">
							<Checkbox
								checked={editedPromotion.stores?.includes('all') || false}
								onCheckedChange={(checked) => {
									if (checked) {
										setEditedPromotion((prev) => ({
											...prev,
											stores: [...(prev.stores?.filter((store) => store !== 'all') || []), 'all']
										}));
									} else {
										setEditedPromotion((prev) => ({
											...prev,
											stores: prev.stores?.filter((store) => store !== 'all') || []
										}));
									}
								}}
							/>
							<span className="text-sm font-medium text-orange-200 group-hover:text-orange-100 light:text-black">
								Todas las tiendas
							</span>
						</label>

						{editedPromotion.stores && editedPromotion.stores.filter((store) => store !== 'all').length > 0 && (
							<div className="max-h-40 space-y-2 overflow-y-auto rounded-xl border border-neutral-800 bg-neutral-900/30 p-3">
								{editedPromotion.stores
									.filter((store) => store !== 'all')
									.map((store, idx) => (
										<div
											key={idx}
											className="group flex items-center justify-between rounded-lg bg-neutral-800/50 p-3 transition-all hover:bg-neutral-800"
										>
											<span className="text-sm text-neutral-200">{store}</span>
											<button
												onClick={() => removeStore(editedPromotion.stores?.indexOf(store) || 0)}
												className="rounded-full p-1 text-neutral-500 opacity-0 transition-all hover:bg-red-500/10 hover:text-red-400 group-hover:opacity-100"
											>
												<X className="h-4 w-4" />
											</button>
										</div>
									))}
							</div>
						)}

						<div className="rounded-xl border border-dashed border-neutral-700 bg-neutral-900/30 p-4">
							<div className="space-y-3">
								<div className="space-y-2">
									<Textarea
										placeholder="Emails de tiendas (uno por línea o separados por comas)"
										value={newStore}
										onChange={setNewStore}
										onSubmit={addStore}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												if (e.ctrlKey || e.metaKey) {
													e.preventDefault();
													addStore();
												} else {
													e.stopPropagation();
												}
											}
										}}
										rows={4}
										containerClassName="w-full"
									/>
									<p className="text-xs text-neutral-400">
										Pegue múltiples emails separados por líneas o comas. Use Ctrl+Enter para agregar.
									</p>
								</div>
								<Button variant="secondary" onClick={addStore} className="w-full">
									<Plus className="h-4 w-4" />
									Agregar Tienda{newStore.includes('\n') || newStore.includes(',') || newStore.includes(';') ? 's' : ''}
								</Button>
							</div>
						</div>
					</div>

					{/* Excluded Stores */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-amber-500/10 p-2">
								<Store className="h-5 w-5 text-amber-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Tiendas Excluidas</h3>
						</div>

						{editedPromotion.excluded_stores && editedPromotion.excluded_stores.length > 0 && (
							<div className="max-h-40 space-y-2 overflow-y-auto rounded-xl border border-neutral-800 bg-neutral-900/30 p-3">
								{editedPromotion.excluded_stores.map((store, idx) => (
									<div
										key={idx}
										className="group flex items-center justify-between rounded-lg bg-neutral-800/50 p-3 transition-all hover:bg-neutral-800"
									>
										<span className="text-sm text-neutral-200">{store}</span>
										<button
											onClick={() => removeExcludedStore(idx)}
											className="rounded-full p-1 text-neutral-500 opacity-0 transition-all hover:bg-red-500/10 hover:text-red-400 group-hover:opacity-100"
										>
											<X className="h-4 w-4" />
										</button>
									</div>
								))}
							</div>
						)}

						<div className="rounded-xl border border-dashed border-neutral-700 bg-neutral-900/30 p-4">
							<div className="space-y-3">
								<div className="space-y-2">
									<Textarea
										placeholder="Emails de tiendas a excluir (uno por línea o separados por comas)"
										value={newExcludedStore}
										onChange={setNewExcludedStore}
										onSubmit={addExcludedStore}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												if (e.ctrlKey || e.metaKey) {
													e.preventDefault();
													addExcludedStore();
												} else {
													e.stopPropagation();
												}
											}
										}}
										rows={4}
										containerClassName="w-full"
									/>
									<p className="text-xs text-neutral-400">
										Pegue múltiples emails separados por líneas o comas. Use Ctrl+Enter para agregar.
									</p>
								</div>
								<Button variant="secondary" onClick={addExcludedStore} className="w-full">
									<Plus className="h-4 w-4" />
									Agregar Tienda{newExcludedStore.includes('\n') || newExcludedStore.includes(',') || newExcludedStore.includes(';') ? 's' : ''} Excluida{newExcludedStore.includes('\n') || newExcludedStore.includes(',') || newExcludedStore.includes(';') ? 's' : ''}
								</Button>
							</div>
						</div>
					</div>

					{/* Brands & Models */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-teal-500/10 p-2">
								<Smartphone className="h-5 w-5 text-teal-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Marcas y Modelos</h3>
						</div>

						{totalBrandsModelsSelected > 0 && (
							<div className="flex items-center justify-between px-3 py-2 bg-teal-500/10 border border-teal-500/20 rounded-lg">
								<span className="text-sm text-teal-300">
									{editedPromotion.brands?.length
										? `${editedPromotion.brands.length} marca${editedPromotion.brands.length > 1 ? 's' : ''}`
										: ''}
									{editedPromotion.brands?.length && editedPromotion.models?.length ? ' + ' : ''}
									{editedPromotion.models?.length
										? `${editedPromotion.models.length} modelo${editedPromotion.models.length > 1 ? 's' : ''} individual${editedPromotion.models.length > 1 ? 'es' : ''}`
										: ''}
								</span>
								<button onClick={clearBrandsModels} className="text-xs text-teal-400 hover:text-teal-300">
									Limpiar selección
								</button>
							</div>
						)}

						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-500 pointer-events-none" />
							<input
								type="text"
								placeholder="Buscar marca o modelo..."
								value={brandsModelsSearch}
								onChange={(e) => setBrandsModelsSearch(e.target.value)}
								className="w-full h-9 pl-9 pr-3 rounded-lg bg-neutral-900 border border-neutral-800 text-sm text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:border-neutral-700 transition-colors"
							/>
						</div>

						{brandsModelsLoading ? (
							<div className="flex items-center justify-center py-8">
								<div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-600 border-t-teal-500" />
							</div>
						) : filteredBrandsModels.length === 0 ? (
							<div className="p-6 text-center text-sm text-neutral-500 border border-neutral-800 rounded-lg bg-neutral-900/50">
								{brandsModelsSearch ? 'No se encontraron resultados' : 'No hay marcas/modelos disponibles'}
							</div>
						) : (
							<div className="max-h-72 overflow-y-auto rounded-xl border border-neutral-800 bg-neutral-900/30">
								{filteredBrandsModels.map((brand) => (
									<div key={brand.brand} className="border-b border-neutral-800/50 last:border-b-0">
										{/* Brand row */}
										<div
											className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-neutral-800/30 transition-colors"
											onClick={() => toggleExpandBrand(brand.brand)}
										>
											<div onClick={(e) => e.stopPropagation()}>
												<Checkbox
													checked={isBrandSelected(brand.brand)}
													onCheckedChange={() => toggleBrand(brand.brand)}
												/>
											</div>
											<span className="flex-1 text-sm font-medium text-neutral-100 capitalize">{brand.brand}</span>
											<span className="text-xs text-neutral-500 mr-2">
												{brand.models.length} modelo{brand.models.length !== 1 ? 's' : ''}
											</span>
											{expandedBrands.has(brand.brand) ? (
												<ChevronUp className="h-4 w-4 text-neutral-500" />
											) : (
												<ChevronDown className="h-4 w-4 text-neutral-500" />
											)}
										</div>

										{/* Models list */}
										{expandedBrands.has(brand.brand) && (
											<div className="pb-2">
												{brand.models.map((model) => {
													const selected = isModelSelected(model.id, brand.brand);
													const brandSelected = isBrandSelected(brand.brand);
													return (
														<label
															key={model.id}
															className={`flex items-center gap-3 pl-10 pr-4 py-2 cursor-pointer transition-colors ${
																brandSelected
																	? 'opacity-60'
																	: selected
																		? 'bg-teal-500/5'
																		: 'hover:bg-neutral-800/20'
															}`}
														>
															<Checkbox
																checked={selected}
																disabled={brandSelected}
																onCheckedChange={() => toggleModel(model.id, brand.brand)}
															/>
															<span className="text-sm text-neutral-300">{model.commercial_name}</span>
														</label>
													);
												})}
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Private audience; never part of the public Promotion object. */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-primary-500/10 p-2"><Users className="h-5 w-5 text-primary-500" /></div>
							<h3 className="text-lg font-semibold text-neutral-100">Cédulas permitidas</h3>
						</div>
						<label className="flex cursor-pointer items-center gap-3 text-sm text-neutral-200">
							<Checkbox
								checked={restricted}
								disabled={saving || deleting}
								onCheckedChange={(checked) => {
									if (checked !== true && restricted) setShowPublicConfirm(true);
									else setRestricted(checked === true);
								}}
							/>
							Solo para estas cédulas
						</label>
						<p className="text-sm text-neutral-300">
							{restricted
								? 'Solo los clientes de esta lista podrán ver y usar la promoción si cumplen sus demás condiciones.'
								: 'Todos los clientes que cumplan las demás condiciones podrán ver la promoción.'}
						</p>
						{restricted && <>
							<p className="text-xs text-neutral-400">{identificationNumbers.length} de 5000 cédulas</p>
							{identificationNumbers.length === 0 && (
								<p className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-3 text-sm text-neutral-200">
									La lista está vacía. Nadie podrá usar la promoción mientras siga restringida.
								</p>
							)}
							{identificationNumbers.length > 0 && (
								<div className="max-h-40 space-y-2 overflow-y-auto rounded-xl border border-neutral-800 bg-neutral-900/30 p-3">
									{identificationNumbers.map((number) => (
										<div key={number} className="flex items-center justify-between rounded-lg bg-neutral-800/50 p-3">
											<span className="font-mono text-sm text-neutral-200">{number}</span>
											<button
												aria-label={`Quitar cédula ${number}`}
												disabled={saving || deleting}
												onClick={() => setIdentificationNumbers((values) => values.filter((value) => value !== number))}
												className="rounded-full p-1 text-neutral-400 hover:bg-red-500/10 hover:text-red-400"
											><X className="h-4 w-4" /></button>
										</div>
									))}
								</div>
							)}
							<div className="rounded-xl border border-dashed border-neutral-700 bg-neutral-900/30 p-4">
								<div className="space-y-3">
									<Textarea
										placeholder="Cédulas (una por línea o separadas por comas)"
										value={newIdentifications}
										onChange={setNewIdentifications}
										onSubmit={(value) => { if (value.trim()) addIdentifications(value); }}
										onKeyDown={(event) => {
											if (event.key === 'Enter') {
												if (event.ctrlKey || event.metaKey) { event.preventDefault(); addIdentifications(); }
												else event.stopPropagation();
											}
										}}
										rows={4}
										containerClassName="w-full"
									/>
									<p className="text-xs text-neutral-400">Pegue varias cédulas separadas por líneas o comas. Use Ctrl+Enter para agregar.</p>
									<Button variant="secondary" disabled={saving || deleting} onClick={() => addIdentifications()} className="w-full">
										<Plus className="h-4 w-4" />Agregar cédulas
									</Button>
								</div>
							</div>
						</>}
					</div>

					{/* IMEI TACs */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-cyan-500/10 p-2">
								<Info className="h-5 w-5 text-cyan-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">IMEI TACs</h3>
						</div>

						{editedPromotion.imei_tacs && editedPromotion.imei_tacs.length > 0 && (
							<div className="max-h-40 space-y-2 overflow-y-auto rounded-xl border border-neutral-800 bg-neutral-900/30 p-3">
								{editedPromotion.imei_tacs.map((tac, idx) => (
									<div
										key={idx}
										className="group flex items-center justify-between rounded-lg bg-neutral-800/50 p-3 transition-all hover:bg-neutral-800"
									>
										<span className="font-mono text-sm text-neutral-200">{tac}</span>
										<button
											onClick={() => removeImeiTac(idx)}
											className="rounded-full p-1 text-neutral-500 opacity-0 transition-all hover:bg-red-500/10 hover:text-red-400 group-hover:opacity-100"
										>
											<X className="h-4 w-4" />
										</button>
									</div>
								))}
							</div>
						)}

						<div className="rounded-xl border border-dashed border-neutral-700 bg-neutral-900/30 p-4">
							<div className="space-y-3">
								<div className="space-y-2">
									<Textarea
										placeholder="IMEI TACs (uno por línea o separados por comas)"
										value={newImeiTac}
										onChange={setNewImeiTac}
										onSubmit={addImeiTac}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												if (e.ctrlKey || e.metaKey) {
													e.preventDefault();
													addImeiTac();
												} else {
													e.stopPropagation();
												}
											}
										}}
										rows={4}
										containerClassName="w-full"
									/>
									<p className="text-xs text-neutral-400">
										Pegue múltiples IMEIs separados por líneas o comas. Use Ctrl+Enter para agregar.
									</p>
								</div>
								<Button variant="secondary" onClick={addImeiTac} className="w-full">
									<Plus className="h-4 w-4" />
									Agregar IMEI TAC{newImeiTac.includes('\n') || newImeiTac.includes(',') || newImeiTac.includes(';') ? 's' : ''}
								</Button>
							</div>
						</div>
					</div>
				</div>
				{/* Actions */}
				<div className="fixed bottom-6 right-6 z-50">
					<div className="flex items-center gap-2 rounded-full border border-neutral-700 bg-neutral-900/90 p-2 shadow-2xl backdrop-blur-md">
						{!isNew && onDelete && (
							<Button
								loading={deleting}
								disabled={saving || deleting}
								variant="secondary"
								onClick={handleDelete}
								className="h-10 rounded-full px-4 text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300"
							>
								<Trash2 className="h-4 w-4" />
								Eliminar
							</Button>
						)}
						<Button
							loading={saving}
							disabled={saving || deleting}
							variant="primary"
							onClick={handleSave}
							className="h-10 rounded-full px-4 text-sm font-medium"
						>
							<Save className="h-4 w-4" />
							{isNew ? 'Crear' : 'Guardar'}
						</Button>
					</div>
				</div>
			</Dialog>
		</>
	);
}

export default function PromotionsConfiguration() {
	const { userData, internalCountry, CountrySettings } = useContext(GlobalContext);
	const country = internalCountry?.get() || 'venezuela';
	const currency_code = CountrySettings?.currency_code;
	const [promos, setPromos] = useState<Promotion[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [expandedImeiTacs, setExpandedImeiTacs] = useState<Record<number, boolean>>({});
	const [expandedBrandsModels, setExpandedBrandsModels] = useState<Record<number, boolean>>({});
	const [expandedDescriptions, setExpandedDescriptions] = useState<Record<number, boolean>>({});
	const [expandedStores, setExpandedStores] = useState<Record<number, boolean>>({});
	const [expandedExcludedStores, setExpandedExcludedStores] = useState<Record<number, boolean>>({});
	const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
	const [creatingPromotion, setCreatingPromotion] = useState(false);
	const [editingSourceLabel, setEditingSourceLabel] = useState<string | null>(null);
	const editorScope = JSON.stringify([country, userData?.email]);
	const editorScopeRef = useRef(editorScope);
	editorScopeRef.current = editorScope;
	const mutationInFlight = useRef(false);
	useEffect(() => {
		setEditingPromotion(null);
		setCreatingPromotion(false);
		setEditingSourceLabel(null);
	}, [editorScope]);
	const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<string>('active');
	const [productTypeFilter, setProductTypeFilter] = useState<string>('all');
	const [levelFilter, setLevelFilter] = useState<string>('all');
	const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
		const saved = localStorage.getItem('promos_view_mode');
		return saved === 'cards' || saved === 'table' ? saved : 'table';
	});

	const levelLabels: Record<(typeof LEVELS)[number], string> = {
		level_1: 'Krece Azul',
		level_2: 'Krece Plata',
		level_3: 'Krece Oro',
		level_4: 'Krece Platino'
	};

	useEffect(() => {
		const unsubscribe = firestore
			.collection('Promotions')
			.doc(country)
			.onSnapshot(
				(doc) => {
					try {
						const data = doc.data() || {};
						const promosData = Object.values(data)
							.toSorted((a, b) => {
								// Consider promotions with is_active === false OR promotion_window.to in the past as inactive
								const now = Date.now() / 1000;
								const aInactive = a.is_active === false || (a.promotion_window?.to && now > a.promotion_window.to.seconds);
								const bInactive = b.is_active === false || (b.promotion_window?.to && now > b.promotion_window.to.seconds);

								if (aInactive !== bInactive) {
									return aInactive ? 1 : -1; // Active promotions first
								}

								return a.label.localeCompare(b.label); // Then alphabetically
							})
							.map((promo) => {
								return {
									...promo,
									...((!promo?.product_types || promo.product_types?.length === 0) && { product_types: ['phone'] })
								};
							});

						setPromos(promosData);
						setLoading(false);
					} catch (error) {
						console.error('Error processing promotions snapshot:', error);
						setAlertDialog({
							title: 'Error',
							message: 'Error al procesar las promociones'
						});
						setLoading(false);
					}
				},
				(error) => {
					console.error('Error listening to promotions:', error);
					setAlertDialog({
						title: 'Error',
						message: 'Error al cargar las promociones'
					});
					setLoading(false);
				}
			);

		// Cleanup subscription on unmount
		return () => unsubscribe();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [country]); // Added country to dependency array

	const toggleImeiTacs = (index: number) => {
		setExpandedImeiTacs((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const toggleBrandsModels = (index: number) => {
		setExpandedBrandsModels((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const toggleDescription = (index: number) => {
		setExpandedDescriptions((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const toggleStores = (index: number) => {
		setExpandedStores((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const toggleExcludedStores = (index: number) => {
		setExpandedExcludedStores((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	async function handleSavePromotion(updatedPromotion: Promotion, originalLabel: string, audience: PromotionAudience) {
		if (mutationInFlight.current) return;
		mutationInFlight.current = true;
		setSaving(true);
		const scope = editorScope;
		if (updatedPromotion.stores?.includes('all')) updatedPromotion.stores = ['all'];
		Object.keys(updatedPromotion).forEach((key) => {
			const value = updatedPromotion[key as keyof Promotion];
			if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
				delete updatedPromotion[key as keyof Promotion];
			}
		});
		try {
			await write_promotion_audience(country, updatedPromotion.label, originalLabel, audience, updatedPromotion);
			if (editorScopeRef.current === scope) { setEditingPromotion(null); setCreatingPromotion(false); }
		} catch (error) {
			if (editorScopeRef.current === scope) setAlertDialog({
				title: 'Error al guardar', message: error instanceof Error ? error.message : 'No se pudo guardar la promoción.'
			});
		} finally { mutationInFlight.current = false; setSaving(false); }
	}

	async function handleDeletePromotion(promotionLabel: string, revision: string) {
		if (mutationInFlight.current) return;
		mutationInFlight.current = true;
		setDeleting(true);
		const scope = editorScope;
		try {
			await write_promotion_audience(country, promotionLabel, promotionLabel, { identification_numbers: null, revision }, null);
			if (editorScopeRef.current === scope) setEditingPromotion(null);
		} catch (error) {
			if (editorScopeRef.current === scope) setAlertDialog({
				title: 'Error al eliminar', message: error instanceof Error ? error.message : 'No se pudo eliminar la promoción.'
			});
		} finally { mutationInFlight.current = false; setDeleting(false); }
	}

	const handleEditPromotion = (promo: Promotion) => {
		setEditingSourceLabel(promo.label);
		setCreatingPromotion(false);
		setEditingPromotion(promo);
	};

	const handleCreatePromotion = () => {
		const newPromotion: Promotion = {
			label: '',
			description: '',
			is_active: true,
			initial_percentage: undefined,
			min_price: undefined,
			approved_credit: undefined,
			payment_options: [],
			product_types: ['phone'],
			stores: [],
			excluded_stores: [],
			imei_tacs: []
		};
		setEditingSourceLabel(null);
		setEditingPromotion(newPromotion);
		setCreatingPromotion(true);
	};

	const handleDuplicatePromotion = (promo: Promotion) => {
		const duplicated: Promotion = {
			...promo,
			label: `${promo.label}_copia`,
			is_active: false
		};
		setEditingSourceLabel(promo.label);
		setEditingPromotion(duplicated);
		setCreatingPromotion(true);
	};

	const getPromoStatus = (promo: Promotion) => {
		const now = Date.now() / 1000;
		if (promo.promotion_window?.from && now < promo.promotion_window.from.seconds) return 'upcoming';
		if (promo.promotion_window?.to && now > promo.promotion_window.to.seconds) return 'expired';
		if (promo.is_active !== false) return 'active';
		return 'inactive';
	};

	// Filter promotions based on search query and filters
	const filteredPromos = promos.filter((promo) => {
		// Status filter
		if (statusFilter !== 'all' && getPromoStatus(promo) !== statusFilter) return false;

		// Product type filter
		if (productTypeFilter !== 'all') {
			if (!promo.product_types?.includes(productTypeFilter)) return false;
		}

		// Level filter
		if (levelFilter !== 'all') {
			const promoLevels = promo.levels || LEVELS;
			if (!promoLevels.includes(levelFilter as (typeof LEVELS)[number])) return false;
		}

		// Text search
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			return (
				promo.label.toLowerCase().includes(query) ||
				(promo.description && promo.description.toLowerCase().includes(query)) ||
				(promo.imei_tacs && promo.imei_tacs.some((tac) => tac.toLowerCase().includes(query))) ||
				(promo.stores && promo.stores.some((store) => store.toLowerCase().includes(query))) ||
				(promo.excluded_stores && promo.excluded_stores.some((store) => store.toLowerCase().includes(query)))
			);
		}

		return true;
	});

	const hasActiveFilters = statusFilter !== 'all' || productTypeFilter !== 'all' || levelFilter !== 'all';

	const clearFilters = () => {
		setStatusFilter('all');
		setProductTypeFilter('all');
		setLevelFilter('all');
		setSearchQuery('');
	};

	type SortKey = 'label' | 'status' | 'dates' | 'initial' | 'credit' | 'plans' | 'types' | 'stores';
	const [sortKey, setSortKey] = useState<SortKey | null>(null);
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

	const handleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortKey(key);
			setSortDir('asc');
		}
	};

	const sortedPromos = useMemo(() => {
		if (!sortKey) return filteredPromos;
		const dir = sortDir === 'asc' ? 1 : -1;

		return [...filteredPromos].sort((a, b) => {
			switch (sortKey) {
				case 'label':
					return dir * a.label.localeCompare(b.label);
				case 'status': {
					const order = { active: 0, upcoming: 1, inactive: 2, expired: 3 };
					return dir * (order[getPromoStatus(a)] - order[getPromoStatus(b)]);
				}
				case 'dates': {
					const aFrom = a.promotion_window?.from?.seconds ?? 0;
					const bFrom = b.promotion_window?.from?.seconds ?? 0;
					return dir * (aFrom - bFrom);
				}
				case 'initial': {
					const aVal = a.initial_percentage ?? -1;
					const bVal = b.initial_percentage ?? -1;
					return dir * (aVal - bVal);
				}
				case 'credit': {
					const aVal = a.approved_credit ?? -1;
					const bVal = b.approved_credit ?? -1;
					if (aVal === Infinity && bVal === Infinity) return 0;
					if (aVal === Infinity) return dir;
					if (bVal === Infinity) return -dir;
					return dir * (aVal - bVal);
				}
				case 'plans': {
					const aVal = a.payment_options?.length ?? 0;
					const bVal = b.payment_options?.length ?? 0;
					return dir * (aVal - bVal);
				}
				case 'types': {
					const aVal = a.product_types?.join(', ') ?? '';
					const bVal = b.product_types?.join(', ') ?? '';
					return dir * aVal.localeCompare(bVal);
				}
				case 'stores': {
					const aVal = a.stores?.includes('all') ? Infinity : (a.stores?.length ?? 0);
					const bVal = b.stores?.includes('all') ? Infinity : (b.stores?.length ?? 0);
					if (aVal === Infinity && bVal === Infinity) return 0;
					if (aVal === Infinity) return dir;
					if (bVal === Infinity) return -dir;
					return dir * (aVal - bVal);
				}
				default:
					return 0;
			}
		});
	}, [filteredPromos, sortKey, sortDir]);

	if (loading) {
		return <ContainerSpinner size={3} />;
	}

	return (
		<div className="mx-auto w-full max-w-7xl space-y-4 p-6">
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			{editingPromotion && (
				<EditPromotionDialog
					key={JSON.stringify([editorScope, editingSourceLabel, creatingPromotion])}
					sourceLabel={editingSourceLabel}
					promotion={editingPromotion}
					onClose={() => {
						setEditingPromotion(null);
						setCreatingPromotion(false);
					}}
					onSave={handleSavePromotion}
					onDelete={!creatingPromotion ? handleDeletePromotion : undefined}
					currency_code={currency_code}
					saving={saving}
					deleting={deleting}
					isNew={creatingPromotion}
				/>
			)}

			{/* Search & Toolbar */}
			<div className="flex items-center gap-2">
				<Input
					placeholder="Buscar promociones..."
					value={searchQuery}
					onChange={setSearchQuery}
					Icon={<Search className="h-4 w-4 text-neutral-400" />}
					containerClassName="flex-1"
				/>
				<div className="flex rounded-lg border border-neutral-800 overflow-hidden shrink-0">
					<button
						onClick={() => { setViewMode('cards'); localStorage.setItem('promos_view_mode', 'cards'); }}
						className={`p-2 transition-colors ${viewMode === 'cards' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500 hover:text-neutral-300'}`}
						title="Vista de tarjetas"
					>
						<LayoutGrid className="h-4 w-4" />
					</button>
					<button
						onClick={() => { setViewMode('table'); localStorage.setItem('promos_view_mode', 'table'); }}
						className={`p-2 transition-colors ${viewMode === 'table' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500 hover:text-neutral-300'}`}
						title="Vista de tabla"
					>
						<List className="h-4 w-4" />
					</button>
				</div>
				<button
					onClick={handleCreatePromotion}
					className="shrink-0 flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
				>
					<Plus className="h-4 w-4" />
					<span className="hidden md:inline">Agregar Promoción</span>
				</button>
			</div>

			{/* Filters */}
			<div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center md:gap-3">
				<Select
					options={[
						{ label: 'Todos los estados', value: 'all' },
						{ label: 'Activa', value: 'active' },
						{ label: 'Inactiva', value: 'inactive' },
						{ label: 'Próxima', value: 'upcoming' },
						{ label: 'Expirada', value: 'expired' }
					]}
					defaultValue={statusFilter}
					onSelectedChange={(v) => setStatusFilter((v as string) || 'all')}
					isDefaultValueNull={false}
					containerClassName="min-w-0"
				/>
				<Select
					options={[
						{ label: 'Todos los tipos', value: 'all' },
						...PROD_TYPES.map((t) => ({ label: t === 'iphone' ? 'iPhone' : t.charAt(0).toUpperCase() + t.slice(1), value: t }))
					]}
					defaultValue={productTypeFilter}
					onSelectedChange={(v) => setProductTypeFilter((v as string) || 'all')}
					isDefaultValueNull={false}
					containerClassName="min-w-0"
				/>
				<Select
					options={[
						{ label: 'Todos los niveles', value: 'all' },
						...LEVELS.map((l) => ({ label: levelLabels[l], value: l }))
					]}
					defaultValue={levelFilter}
					onSelectedChange={(v) => setLevelFilter((v as string) || 'all')}
					isDefaultValueNull={false}
					containerClassName="min-w-0"
				/>
				{hasActiveFilters && (
					<button
						onClick={clearFilters}
						className="text-xs text-primary-400 hover:text-primary-300 transition-colors text-center"
					>
						Limpiar filtros
					</button>
				)}
			</div>

			{/* Main Content Container */}
			{filteredPromos.length === 0 ? (
				searchQuery.trim() || hasActiveFilters ? (
					<div className="py-12 text-center">
						<Filter className="mx-auto h-12 w-12 text-neutral-400" />
						<h3 className="mt-4 text-lg font-semibold text-neutral-200">No se encontraron resultados</h3>
						<p className="text-neutral-400">No hay promociones que coincidan con los filtros aplicados.</p>
					</div>
				) : (
					<div className="py-12 text-center">
						<Tag className="mx-auto h-12 w-12 text-neutral-400" />
						<h3 className="mt-4 text-lg font-semibold text-neutral-200">No hay promociones registradas</h3>
						<p className="text-neutral-400">Crea tu primera promoción para comenzar.</p>
					</div>
				)
			) : viewMode === 'table' ? (
				<div className="overflow-x-auto rounded-lg border border-neutral-800">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-neutral-800 bg-neutral-900">
								{([
									['label', 'Nombre', ''],
									['status', 'Estado', ''],
									['dates', 'Vigencia', 'hidden md:table-cell'],
									['initial', 'Inicial', 'hidden lg:table-cell'],
									['credit', 'Crédito', 'hidden lg:table-cell'],
									['plans', 'Planes', 'hidden md:table-cell'],
									['types', 'Tipos', 'hidden xl:table-cell'],
									['stores', 'Tiendas', 'hidden xl:table-cell'],
								] as [SortKey, string, string][]).map(([key, label, hide]) => (
									<th
										key={key}
										onClick={() => handleSort(key)}
										className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400 cursor-pointer select-none hover:text-neutral-200 transition-colors ${hide}`}
									>
										<div className="flex items-center gap-1">
											{label}
											{sortKey === key ? (
												sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
											) : (
												<ArrowUpDown className="h-3 w-3 opacity-30" />
											)}
										</div>
									</th>
								))}
								<th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-400 w-20" />
							</tr>
						</thead>
						<tbody>
							{sortedPromos.map((promo, index) => {
								const promoStatus = getPromoStatus(promo);
								return (
									<tr
										key={index}
										onClick={() => handleEditPromotion(promo)}
										className="border-b border-neutral-800/50 last:border-b-0 cursor-pointer transition-colors hover:bg-neutral-800/20"
									>
										<td className="px-4 py-3">
											<div className="font-medium text-neutral-100 truncate max-w-[200px]">{promo.label}</div>
											<div className="text-xs text-neutral-500 truncate max-w-[200px]">{promo.description}</div>
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
													promoStatus === 'upcoming'
														? 'bg-yellow-900 text-yellow-300'
														: promoStatus === 'expired'
															? 'bg-red-900 text-red-300'
															: promoStatus === 'active'
																? 'bg-green-900 text-green-300'
																: 'bg-red-900 text-red-300'
												}`}
											>
												{promoStatus === 'upcoming'
													? 'Próxima'
													: promoStatus === 'expired'
														? 'Expirada'
														: promoStatus === 'active'
															? 'Activa'
															: 'Inactiva'}
											</span>
										</td>
										<td className="px-4 py-3 hidden md:table-cell">
											<span className={promo.promotion_window?.from && promo.promotion_window?.to ? 'text-neutral-300' : 'text-neutral-600 italic'}>
												{promo.promotion_window?.from && promo.promotion_window?.to
													? `${new Date(promo.promotion_window.from.seconds * 1000).toLocaleDateString()} - ${new Date(promo.promotion_window.to.seconds * 1000).toLocaleDateString()}`
													: '-'}
											</span>
										</td>
										<td className="px-4 py-3 hidden lg:table-cell text-neutral-300">
											{promo.initial_percentage !== undefined ? `${promo.initial_percentage * 100}%` : <span className="text-neutral-600">-</span>}
										</td>
										<td className="px-4 py-3 hidden lg:table-cell text-neutral-300">
											{promo.approved_credit !== undefined
												? isFinite(promo.approved_credit)
													? formatCurrency(promo.approved_credit, currency_code)
													: 'Sin límite'
												: <span className="text-neutral-600">-</span>}
										</td>
										<td className="px-4 py-3 hidden md:table-cell text-neutral-300">
											{promo.payment_options && promo.payment_options.length > 0
												? promo.payment_options.map((o) => `${o.installments}c`).join(', ')
												: <span className="text-neutral-600">-</span>}
										</td>
										<td className="px-4 py-3 hidden xl:table-cell">
											<div className="flex flex-wrap gap-1">
												{promo.product_types?.map((type, idx) => (
													<span key={idx} className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] text-neutral-300">
														{type}
													</span>
												))}
											</div>
										</td>
										<td className="px-4 py-3 hidden xl:table-cell text-neutral-300">
											{promo.stores?.includes('all')
												? 'Todas'
												: promo.stores && promo.stores.length > 0
													? promo.stores.length
													: <span className="text-neutral-600">-</span>}
										</td>
										<td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
											<div className="flex items-center justify-end gap-1">
												<button
													onClick={() => handleDuplicatePromotion(promo)}
													className="rounded-full p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-300"
													title="Duplicar"
												>
													<Copy className="h-3.5 w-3.5" />
												</button>
												<button
													onClick={() => handleEditPromotion(promo)}
													className="rounded-full p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-300"
													title="Editar"
												>
													<Pencil className="h-3.5 w-3.5" />
												</button>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			) : (
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{/* Existing Promotions */}
					{sortedPromos.map((promo, index) => {
					return (
						<div
							key={index}
							className={`rounded-lg border p-4 ${'border-neutral-900 bg-neutral-1000 dark:border-neutral-800 dark:bg-background'}`}
						>
							<div className="mb-4 flex items-start justify-between gap-2">
								<div className="flex-grow">
									<h2 className={`mb-1 truncate text-lg font-semibold ${'text-primary-500 dark:text-primary-400'}`}>
										{promo.label}
									</h2>
									<div className={`relative ${expandedDescriptions[index] ? 'min-h-12' : 'h-12'}`}>
										<p
											className={`cursor-pointer text-sm transition-all duration-200 hover:opacity-80 ${'text-neutral-300 dark:text-neutral-400'} ${expandedDescriptions[index] ? 'line-clamp-none' : 'line-clamp-2 overflow-hidden'}`}
											onClick={() => toggleDescription(index)}
											style={{
												display: '-webkit-box',
												WebkitLineClamp: expandedDescriptions[index] ? 'unset' : 2,
												WebkitBoxOrient: 'vertical',
												overflow: expandedDescriptions[index] ? 'visible' : 'hidden'
											}}
											title="Click para expandir/contraer"
										>
											{promo.description}
										</p>
									</div>
								</div>
								<div className="flex shrink-0 items-center gap-1">
									<button
										onClick={() => handleDuplicatePromotion(promo)}
										className="flex flex-shrink-0 items-center rounded-full p-1 text-xs font-medium transition-colors hover:bg-neutral-800"
										title="Duplicar promoción"
									>
										<Copy className="h-4 w-4 text-neutral-400" />
									</button>
									<button
										onClick={() => handleEditPromotion(promo)}
										className="flex flex-shrink-0 items-center rounded-full p-1 text-xs font-medium transition-colors hover:bg-neutral-800"
										title="Editar promoción"
									>
										<Pencil className="h-4 w-4 text-neutral-400" />
									</button>
									<span
										className={`flex flex-shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
											promo.promotion_window?.from && Date.now() / 1000 < promo.promotion_window.from.seconds
												? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
												: promo.promotion_window?.to && Date.now() / 1000 > promo.promotion_window.to.seconds
													? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
													: promo.is_active !== false
														? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
														: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
										}`}
									>
										{promo.promotion_window?.from && Date.now() / 1000 < promo.promotion_window.from.seconds ? (
											<>
												<Clock className="mr-1 h-3 w-3" />
												Próxima
											</>
										) : promo.promotion_window?.to && Date.now() / 1000 > promo.promotion_window.to.seconds ? (
											<>
												<XCircle className="mr-1 h-3 w-3" />
												Expirada
											</>
										) : promo.is_active !== false ? (
											<>
												<CheckCircle className="mr-1 h-3 w-3" />
												Activa
											</>
										) : (
											<>
												<XCircle className="mr-1 h-3 w-3" />
												Inactiva
											</>
										)}
									</span>
								</div>
							</div>

							<div className="space-y-3">
								<div className="flex items-center text-sm">
									<Calendar className={`mr-2 h-4 w-4 flex-shrink-0 ${'text-primary-500'}`} />

									<span
										className={`${
											promo.promotion_window?.from && promo.promotion_window?.to
												? 'font-bold text-neutral-100 dark:text-neutral-200'
												: 'italic text-neutral-500 dark:text-neutral-500'
										}`}
									>
										{promo.promotion_window?.from && promo.promotion_window?.to
											? `${new Date(promo.promotion_window.from.seconds * 1000).toLocaleDateString()} - ${new Date(promo.promotion_window.to.seconds * 1000).toLocaleDateString()}`
											: 'No configurado'}{' '}
									</span>
									<span></span>
								</div>

								{/* Initial Percentage - Always shown */}
								<div className="flex items-center text-sm">
									<Tag className={`mr-2 h-4 w-4 flex-shrink-0 ${'text-primary-500'}`} />
									<span className={`font-medium ${'text-neutral-100 dark:text-neutral-200'}`}>Inicial:&nbsp;</span>
									<span
										className={`${
											promo.initial_percentage !== undefined
												? 'text-neutral-300 dark:text-neutral-400'
												: 'italic text-neutral-500 dark:text-neutral-500'
										}`}
									>
										{promo.initial_percentage !== undefined ? `${promo.initial_percentage * 100}%` : 'No configurado'}
									</span>
								</div>

								{/* Approved Credit - Always shown */}
								<div className="flex items-center text-sm">
									<CreditCard className={`mr-2 h-4 w-4 flex-shrink-0 ${'text-primary-500'}`} />
									<span className={`font-medium ${'text-neutral-100 dark:text-neutral-200'}`}>Crédito:&nbsp;</span>
									<span
										className={`${
											promo.approved_credit !== undefined
												? 'text-neutral-300 dark:text-neutral-400'
												: 'italic text-neutral-500 dark:text-neutral-500'
										}`}
									>
										{promo.approved_credit !== undefined
											? isFinite(promo.approved_credit)
												? formatCurrency(promo.approved_credit, currency_code)
												: 'Sin límite'
											: 'No configurado'}
									</span>
								</div>

								{/* Min Price - Always shown */}
								<div className="flex items-center text-sm">
									<DollarSign className={`mr-2 h-4 w-4 flex-shrink-0 ${'text-primary-500'}`} />
									<span className={`font-medium ${'text-neutral-100 dark:text-neutral-200'}`}>Precio Mínimo:&nbsp;</span>
									<span
										className={`${
											promo.min_price !== undefined
												? 'text-neutral-300 dark:text-neutral-400'
												: 'italic text-neutral-500 dark:text-neutral-500'
										}`}
									>
										{promo.min_price !== undefined ? formatCurrency(promo.min_price, currency_code) : 'No configurado'}
									</span>
								</div>

								{/* Payment Options - Always shown */}
								<div>
									<div className={`mb-1 flex items-center text-sm font-medium ${'text-neutral-100 dark:text-neutral-200'}`}>
										<CreditCard className={`mr-2 h-4 w-4 ${'text-primary-500'}`} />
										<span>Planes de Pago:</span>
									</div>
									<div className="ml-6 space-y-1">
										{promo.payment_options && promo.payment_options.length > 0 ? (
											promo.payment_options.map((option, idx) => (
												<div
													key={idx}
													className={`flex items-center justify-between text-xs ${'text-neutral-300 dark:text-neutral-400'}`}
												>
													<span>{option.installments} cuotas</span>
													<span className="rounded-full bg-primary-200 px-2 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-300">
														{(option.factor * 100 - 100) % 1 === 0
															? (option.factor * 100 - 100).toFixed(0)
															: (option.factor * 100 - 100).toFixed(1)}
														% interés
													</span>
												</div>
											))
										) : (
											<span className={`text-xs italic ${'text-neutral-500 dark:text-neutral-500'}`}>No configurado</span>
										)}
									</div>
								</div>

								{/* Product Types - Always shown */}
								<div>
									<div className={`mb-1 flex items-center text-sm font-medium ${'text-neutral-100 dark:text-neutral-200'}`}>
										<Info className={`mr-2 h-4 w-4 ${'text-primary-500'}`} />
										<span>Tipos de Producto:</span>
									</div>
									<div className="ml-6 flex flex-wrap gap-2">
										{promo.product_types?.map((type, idx) => (
											<span
												key={idx}
												className={`rounded-full px-2 py-0.5 text-xs ${'bg-neutral-700 text-neutral-200 dark:bg-neutral-800 dark:text-neutral-300'}`}
											>
												{type}
											</span>
										))}
									</div>
								</div>

								{/* Levels - Always shown */}
								<div>
									<div className={`mb-1 flex items-center text-sm font-medium ${'text-neutral-100 dark:text-neutral-200'}`}>
										<Star className={`mr-2 h-4 w-4 ${'text-amber-500'}`} />
										<span>Niveles:</span>
									</div>
									<div className="ml-6 flex flex-wrap gap-2">
										{!promo.levels || promo.levels.length === 0 || promo.levels.length === LEVELS.length ? (
											<span className={`rounded-full px-2 py-0.5 text-xs ${'bg-neutral-700 text-neutral-200 dark:bg-neutral-800 dark:text-neutral-300'}`}>
												Todos los niveles
											</span>
										) : (
											promo.levels.map((level, idx) => (
												<span
													key={idx}
													className={`rounded-full px-2 py-0.5 text-xs ${'bg-neutral-700 text-neutral-200 dark:bg-neutral-800 dark:text-neutral-300'}`}
												>
													{levelLabels[level]}
												</span>
											))
										)}
									</div>
								</div>

								{/* Stores - Always shown */}
								<div>
									<div
										className={`mb-1 flex cursor-pointer items-center justify-between text-sm font-medium ${'text-neutral-100 dark:text-neutral-200'} ${promo.stores && promo.stores.length > 1 ? 'cursor-pointer' : 'cursor-default'}`}
										onClick={() => promo.stores && promo.stores.length > 1 && toggleStores(index)}
									>
										<div className="flex items-center">
											<Store className={`mr-2 h-4 w-4 ${'text-primary-500'}`} />
											<span>Tiendas Aplicables {promo.stores && promo.stores.length > 0 ? `(${promo.stores.length})` : ''}</span>
										</div>
										{promo.stores && promo.stores.length > 1 && (
											<>{expandedStores[index] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}</>
										)}
									</div>
									{promo.stores && promo.stores.length > 0 ? (
										(promo.stores.length === 1 || expandedStores[index]) && (
											<div className="ml-6 mt-2 flex flex-wrap gap-2">
												{promo.stores.map((store, idx) => (
													<span
														key={idx}
														className={`max-w-full truncate rounded-full px-3 py-1 text-xs ${'bg-neutral-700 text-neutral-200 dark:bg-neutral-800 dark:text-neutral-300'}`}
														title={store}
													>
														{store}
													</span>
												))}
											</div>
										)
									) : (
										<div className="ml-6 mt-1">
											<span className={`text-xs italic ${'text-neutral-500 dark:text-neutral-500'}`}>No configurado</span>
										</div>
									)}
								</div>

								{/* Excluded Stores - Always shown */}
								<div>
									<div
										className={`mb-1 flex cursor-pointer items-center justify-between text-sm font-medium ${'text-neutral-100 dark:text-neutral-200'} ${promo.excluded_stores && promo.excluded_stores.length > 1 ? 'cursor-pointer' : 'cursor-default'}`}
										onClick={() => promo.excluded_stores && promo.excluded_stores.length > 1 && toggleExcludedStores(index)}
									>
										<div className="flex items-center">
											<Store className={`mr-2 h-4 w-4 ${'text-amber-500'}`} />
											<span>
												Tiendas Excluidas{' '}
												{promo.excluded_stores && promo.excluded_stores.length > 0 ? `(${promo.excluded_stores.length})` : ''}
											</span>
										</div>
										{promo.excluded_stores && promo.excluded_stores.length > 1 && (
											<>
												{expandedExcludedStores[index] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
											</>
										)}
									</div>
									{promo.excluded_stores && promo.excluded_stores.length > 0 ? (
										(promo.excluded_stores.length === 1 || expandedExcludedStores[index]) && (
											<div className="ml-6 mt-2 flex flex-wrap gap-2">
												{promo.excluded_stores.map((store, idx) => (
													<span
														key={idx}
														className={`max-w-full truncate rounded-full px-3 py-1 text-xs ${'bg-red-800/50 text-red-200 dark:bg-red-900/40 dark:text-red-300'}`}
														title={store}
													>
														{store}
													</span>
												))}
											</div>
										)
									) : (
										<div className="ml-6 mt-1">
											<span className={`text-xs italic ${'text-neutral-500 dark:text-neutral-500'}`}>No configurado</span>
										</div>
									)}
								</div>

								{/* Brands & Models - Always shown */}
								<div>
									<div
										className={`mb-1 flex cursor-pointer items-center justify-between text-sm font-medium ${'text-neutral-100 dark:text-neutral-200'}`}
										onClick={() => toggleBrandsModels(index)}
									>
										<div className="flex items-center">
											<Smartphone className={`mr-2 h-4 w-4 ${'text-primary-500'}`} />
											<span>
												Marcas y Modelos
												{(promo.brands?.length || 0) + (promo.models?.length || 0) > 0
													? ` (${promo.brands?.length || 0} marca${(promo.brands?.length || 0) === 1 ? '' : 's'}, ${promo.models?.length || 0} modelo${(promo.models?.length || 0) === 1 ? '' : 's'})`
													: ''}
											</span>
										</div>
										{(promo.brands?.length || 0) + (promo.models?.length || 0) > 0 && (
											<>{expandedBrandsModels[index] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}</>
										)}
									</div>
									{(promo.brands?.length || 0) + (promo.models?.length || 0) > 0 ? (
										expandedBrandsModels[index] && (
											<div className="ml-6 mt-2 flex flex-wrap gap-2">
												{promo.brands?.map((b, idx) => (
													<span
														key={`b-${idx}`}
														className="max-w-full truncate rounded-full bg-teal-900/40 px-3 py-1 text-xs capitalize text-teal-200"
														title={b}
													>
														{b}
													</span>
												))}
												{promo.models?.map((m, idx) => (
													<span
														key={`m-${idx}`}
														className="max-w-full truncate rounded-full bg-neutral-700 px-3 py-1 text-xs text-neutral-200 dark:bg-neutral-800 dark:text-neutral-300"
														title={m}
													>
														{m}
													</span>
												))}
											</div>
										)
									) : (
										<div className="ml-6 mt-1">
											<span className={`text-xs italic ${'text-neutral-500 dark:text-neutral-500'}`}>No configurado</span>
										</div>
									)}
								</div>

								{/* IMEI TACs - Always shown */}
								<div>
									<div
										className={`mb-1 flex cursor-pointer items-center justify-between text-sm font-medium ${'text-neutral-100 dark:text-neutral-200'}`}
										onClick={() => toggleImeiTacs(index)}
									>
										<div className="flex items-center">
											<Info className={`mr-2 h-4 w-4 ${'text-primary-500'}`} />
											<span>IMEI TACs {promo.imei_tacs && promo.imei_tacs.length > 0 ? `(${promo.imei_tacs.length})` : ''}</span>
										</div>
										{promo.imei_tacs && promo.imei_tacs.length > 0 && (
											<>{expandedImeiTacs[index] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}</>
										)}
									</div>
									{promo.imei_tacs && promo.imei_tacs.length > 0 ? (
										expandedImeiTacs[index] && (
											<div className="ml-6 mt-2 flex flex-wrap gap-2">
												{promo.imei_tacs.map((tac, idx) => (
													<span
														key={idx}
														className={`max-w-full truncate rounded-full px-3 py-1 text-xs ${'bg-neutral-700 text-neutral-200 dark:bg-neutral-800 dark:text-neutral-300'}`}
														title={tac}
													>
														{tac}
													</span>
												))}
											</div>
										)
									) : (
										<div className="ml-6 mt-1">
											<span className={`text-xs italic ${'text-neutral-500 dark:text-neutral-500'}`}>No configurado</span>
										</div>
									)}
								</div>
							</div>
						</div>
					);
				})}
				</div>
			)}
		</div>
	);
}
